import { PackageIcon } from '@phosphor-icons/react';
import { T } from 'gt-react';

import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import { useProduct } from 'src/api/inventory-management/products/useProduct';
import useProductVariant from 'src/api/inventory-management/products/useProductVariant';

interface ProductPillProps {
  productId?: string;
  productVariantId?: string;
  quantity?: number;
  hideIcon?: boolean;
  sx?: React.CSSProperties;
  textSx?: React.CSSProperties;
}

const ProductPill: React.FC<ProductPillProps> = ({
  productId,
  productVariantId,
  quantity,
  hideIcon,
  sx,
  textSx,
}) => {
  const {
    data: fetchedVariantData,
    isLoading: isLoadingVariant,
    error: variantError,
  } = useProductVariant(productVariantId || '', {
    enabled: !productId && !!productVariantId,
  });

  const effectiveProductId =
    productId || fetchedVariantData?.productVariant?.productId;

  const {
    data: fetchedProductData,
    isLoading: isLoadingProduct,
    error: productError,
  } = useProduct(
    effectiveProductId,
    {
      includes: [],
    },
    !!effectiveProductId,
  );

  const isLoading = isLoadingProduct || isLoadingVariant;
  const error = productError || variantError;

  // Display loading state if we're waiting for variant data to get the product ID
  const isPendingProductFetch =
    !productId && isLoadingVariant && !fetchedVariantData;

  if (error) {
    return (
      <MDBox
        sx={{
          fontSize: 12,
          backgroundColor: 'rgba(244, 67, 54, 0.08)',
          borderRadius: '16px',
          padding: '4px 8px',
          width: 'fit-content',
        }}
      >
        <MDTypography color="error.main" sx={{ fontSize: 12 }}>
          <T context="error state message when a product fails to load">
            Error loading product
          </T>
        </MDTypography>
      </MDBox>
    );
  }

  if (isLoading || !fetchedProductData?.product || isPendingProductFetch) {
    return (
      <MDBox
        sx={{
          fontSize: 12,
          backgroundColor: 'rgba(76, 175, 80, 0.08)',
          borderRadius: '16px',
          padding: '4px 8px',
          width: 'fit-content',
        }}
      >
        <MDTypography color="success.main" sx={{ fontSize: 12 }}>
          Loading...
        </MDTypography>
      </MDBox>
    );
  }

  const handleModalOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (productId || productVariantId) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const handleContainerClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation(); // Stop event from bubbling
  };

  return (
    <MDBox
      display="flex"
      alignItems="center"
      onClick={handleContainerClick}
      sx={{ cursor: 'default' }}
    >
      <MDBox
        data-clickable-id={productId || productVariantId}
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
          fontSize: 12,
          backgroundColor: 'rgba(76, 175, 80, 0.08)',
          color: 'success.main',
          borderRadius: '16px',
          padding: '4px 8px',
          width: 'fit-content',
          cursor: productId || productVariantId ? 'pointer' : undefined,
          transition: 'all 0.2s ease-in-out',
          ...((productId || productVariantId) && {
            '&:hover': {
              backgroundColor: 'rgba(76, 175, 80, 0.16)',
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: 'none',
            },
          }),
          ...(sx || {}),
        }}
        onClick={handleModalOpen}
      >
        {!hideIcon && <PackageIcon size={16} />}

        <MDTypography
          color="success.main"
          sx={{ fontSize: 12, ...(textSx || {}) }}
        >
          {quantity ? `${quantity}x ` : ''}
          {fetchedProductData.product.name}
          {/* {unitVariation &&
            unitVariation.unitOfMeasure &&
            ` (${unitVariation.unitOfMeasure})`} */}
        </MDTypography>
      </MDBox>

      {/* {isModalOpen && fetchedProduct && (
        <ProductModal
          product={fetchedProduct.product}
          onClose={handleModalClose}
        />
      )} */}
    </MDBox>
  );
};

export default ProductPill;
